// Generic vertical engine config. Each vertical reuses one listing/detail/post flow.
// icon = lucide icon name; specFields go into `specs` on the backend.

export const VERTICALS = {
  bikes: {
    slug: "bikes",
    label: "Bikes",
    icon: "Bike",
    accent: "bikes",
    heading: "Motorbikes & scooters from verified sellers",
    tagline: "Superbikes, commuters & scooters — verified deals across India.",
    sellCta: "Sell your bike",
    postHeading: "Sell your bike",
    postSub: "List your motorcycle on VYAPAAR — free & verified.",
    emptyLabel: "bikes",
    // badge shown on the card image
    badgeKey: "fuel_type",
    // filters on the listing page (selects). price/state/q are always present.
    filters: [
      { param: "brand", label: "Brand", options: ["Royal Enfield", "Bajaj", "Hero", "Honda", "TVS", "Yamaha", "KTM", "Suzuki", "Jawa", "Other"] },
      { param: "fuel_type", label: "Fuel", options: ["Petrol", "Electric"] },
    ],
    // spec fields for the post form (stored under specs)
    specFields: [
      { key: "brand", label: "Brand", type: "select", options: ["Royal Enfield", "Bajaj", "Hero", "Honda", "TVS", "Yamaha", "KTM", "Suzuki", "Jawa", "Other"], required: true },
      { key: "model", label: "Model", type: "text", placeholder: "e.g. Classic 350", required: true },
      { key: "year", label: "Year", type: "number", required: true },
      { key: "km_driven", label: "KM Driven", type: "number", required: true },
      { key: "engine_cc", label: "Engine (cc)", type: "number", required: true },
      { key: "fuel_type", label: "Fuel", type: "select", options: ["Petrol", "Electric"], required: true },
      { key: "owners", label: "Owners", type: "number", min: 1 },
    ],
    // specs displayed on card / detail: {icon, label, format}
    cardSpecs: [
      { icon: "Calendar", format: (s) => s.year },
      { icon: "Gauge", format: (s) => `${Number(s.km_driven || 0).toLocaleString("en-IN")} km` },
      { icon: "Zap", format: (s) => `${s.engine_cc} cc` },
      { icon: "User", format: (s) => `${s.owners} owner` },
    ],
    detailSpecs: [
      { icon: "Calendar", label: "Year", format: (s) => s.year },
      { icon: "Gauge", label: "KM Driven", format: (s) => `${Number(s.km_driven || 0).toLocaleString("en-IN")} km` },
      { icon: "Zap", label: "Engine", format: (s) => `${s.engine_cc} cc` },
      { icon: "Fuel", label: "Fuel", format: (s) => s.fuel_type },
      { icon: "User", label: "Owners", format: (s) => `${s.owners}` },
      { icon: "Bike", label: "Model", format: (s) => s.model },
    ],
  },

  parts: {
    slug: "parts",
    label: "Parts",
    icon: "Wrench",
    accent: "parts",
    heading: "Genuine spare parts for cars & bikes",
    tagline: "New & used auto parts and spares from verified sellers.",
    sellCta: "Sell a part",
    postHeading: "Sell a spare part",
    postSub: "List genuine parts on VYAPAAR — free & verified.",
    emptyLabel: "parts",
    badgeKey: "condition",
    filters: [
      { param: "category", label: "Category", options: ["Engine", "Electrical", "Body", "Suspension", "Brakes", "Tyres & Wheels", "Interior", "Accessories", "Other"] },
      { param: "condition", label: "Condition", options: ["New", "Used", "Refurbished"] },
    ],
    specFields: [
      { key: "category", label: "Category", type: "select", options: ["Engine", "Electrical", "Body", "Suspension", "Brakes", "Tyres & Wheels", "Interior", "Accessories", "Other"], required: true },
      { key: "brand", label: "Brand", type: "text", placeholder: "e.g. Bosch / OEM", required: true },
      { key: "condition", label: "Condition", type: "select", options: ["New", "Used", "Refurbished"], required: true },
      { key: "compatible", label: "Compatible With", type: "text", placeholder: "e.g. Maruti Swift 2015-2020" },
      { key: "quantity", label: "Quantity Available", type: "number", min: 1 },
    ],
    cardSpecs: [
      { icon: "Tag", format: (s) => s.category },
      { icon: "Package", format: (s) => `Qty ${s.quantity || 1}` },
      { icon: "Wrench", format: (s) => s.brand },
      { icon: "Car", format: (s) => s.compatible || "Universal" },
    ],
    detailSpecs: [
      { icon: "Tag", label: "Category", format: (s) => s.category },
      { icon: "Wrench", label: "Brand", format: (s) => s.brand },
      { icon: "ShieldCheck", label: "Condition", format: (s) => s.condition },
      { icon: "Car", label: "Compatible", format: (s) => s.compatible || "Universal" },
      { icon: "Package", label: "Quantity", format: (s) => `${s.quantity || 1}` },
    ],
  },

  materials: {
    slug: "materials",
    label: "Building Materials",
    icon: "Hammer",
    accent: "materials",
    heading: "Cement, steel, bricks & more — direct from suppliers",
    tagline: "Everything to build your home, from verified suppliers.",
    sellCta: "List a material",
    postHeading: "List a building material",
    postSub: "Sell construction materials on VYAPAAR — free & verified.",
    emptyLabel: "materials",
    badgeKey: "material_type",
    priceSuffix: (s) => (s?.unit ? `/${s.unit.toLowerCase()}` : ""),
    filters: [
      { param: "material_type", label: "Material", options: ["Cement", "Steel / TMT", "Bricks", "Sand", "Aggregate", "Tiles", "Paint", "Plywood", "Pipes & Fittings", "Other"] },
      { param: "unit", label: "Unit", options: ["Bag", "Ton", "Sqft", "Piece", "Litre", "Kg"] },
    ],
    specFields: [
      { key: "material_type", label: "Material Type", type: "select", options: ["Cement", "Steel / TMT", "Bricks", "Sand", "Aggregate", "Tiles", "Paint", "Plywood", "Pipes & Fittings", "Other"], required: true },
      { key: "brand", label: "Brand", type: "text", placeholder: "e.g. UltraTech", required: true },
      { key: "unit", label: "Unit", type: "select", options: ["Bag", "Ton", "Sqft", "Piece", "Litre", "Kg"], required: true },
      { key: "min_order_qty", label: "Min Order Qty", type: "number", min: 1 },
    ],
    cardSpecs: [
      { icon: "Layers", format: (s) => s.material_type },
      { icon: "Ruler", format: (s) => `Per ${s.unit}` },
      { icon: "Tag", format: (s) => s.brand },
      { icon: "Package", format: (s) => `Min ${s.min_order_qty || 1}` },
    ],
    detailSpecs: [
      { icon: "Layers", label: "Material", format: (s) => s.material_type },
      { icon: "Tag", label: "Brand", format: (s) => s.brand },
      { icon: "Ruler", label: "Unit", format: (s) => s.unit },
      { icon: "Package", label: "Min Order", format: (s) => `${s.min_order_qty || 1} ${s.unit}` },
    ],
  },
};

export const getVertical = (slug) => VERTICALS[slug] || null;
